/**
 * The receipt logo as the print head burns it — a port of the gateway's
 * raster path, so the preview shows the actual dots.
 *
 * Where the logo comes from: `websiteConfig.branding.logo` (set in the website
 * editor's branding), resolved server-side by packages/core
 * `receiptLogoLookup` — fitted inside 576 x 320 dots at 80 mm (384 at 58 mm,
 * NOTHING at 76 mm: the impact printer has no raster path), flattened onto
 * white and written as a greyscale PNG. Management reads the same bytes from
 * `GET /print/receipt-logo`.
 *
 * What the gateway does with them (apps/gateway/internal/renderer/raster.go),
 * ported statement for statement:
 *
 *   toGrayFitted  fit inside dots x 320, never upscale; x/image/draw CatmullRom
 *                 (the Gray → RGBA Src path: scaleX_Gray + scaleY_RGBA_Src);
 *                 centred by padding on a white canvas exactly `dots` wide
 *   dither        Floyd–Steinberg, float error buffer, threshold 128
 *   escposRaster  GS v 0 rows, MSB = leftmost dot, set bit = black
 *
 * JavaScript numbers are IEEE doubles with no fused multiply-add, and every
 * multiply-add on the Go side is written with an explicit float64() conversion
 * (x/image/draw's own, and raster.go's dither), so the arithmetic is the same
 * operation for operation. The preview-grid logo corpus
 * (packages/print-conformance/fixtures/preview-grid/logo-samples.json) pins it
 * bit for bit against the gateway.
 */

/** A decoded greyscale image, row-major, one byte per pixel (image.Gray.Pix). */
export interface GrayImage {
  readonly width: number;
  readonly height: number;
  readonly pix: Uint8Array;
}

/** raster.go `logoMaxHeightDots`. */
export const LOGO_MAX_HEIGHT_DOTS = 320;

/** raster.go `dotsForPaperWidth`: printable raster dots, or null for no raster path (76 mm impact). */
export function rasterDotsForPaperWidth(mm: number): number | null {
  if (mm === 58) return 384;
  if (mm === 76) return null;
  return 576;
}

// ─── x/image/draw CatmullRom ────────────────────────────────────────────────

const SUPPORT = 2;

function catmullRom(t: number): number {
  if (t < 1) return (1.5 * t - 2.5) * t * t + 1;
  return ((-0.5 * t + 2.5) * t - 4) * t + 2;
}

interface Distrib {
  readonly sources: { i: number; j: number; invTotalWeight: number; invTotalWeightFFFF: number }[];
  readonly coords: number[];
  readonly weights: number[];
}

/** scale.go `newDistrib`. */
function newDistrib(dw: number, sw: number): Distrib {
  const scale = sw / dw;
  let halfWidth = SUPPORT;
  let kernelArgScale = 1;
  if (scale > 1) {
    halfWidth *= scale;
    kernelArgScale = 1 / scale;
  }
  const bounds: { i: number; j: number; center: number }[] = [];
  for (let x = 0; x < dw; x += 1) {
    const center = (x + 0.5) * scale - 0.5;
    let i = Math.floor(center - halfWidth);
    if (i < 0) i = 0;
    let j = Math.ceil(center + halfWidth);
    if (j > sw) {
      j = sw;
      if (j < i) j = i;
    }
    bounds.push({ i, j, center });
  }
  const coords: number[] = [];
  const weights: number[] = [];
  const sources = bounds.map((b) => {
    let totalWeight = 0;
    const l = coords.length;
    for (let coord = b.i; coord < b.j; coord += 1) {
      const t = Math.abs((b.center - coord) * kernelArgScale);
      if (t >= SUPPORT) continue;
      const weight = catmullRom(t);
      if (weight === 0) continue;
      totalWeight += weight;
      coords.push(coord);
      weights.push(weight);
    }
    totalWeight = 1 / totalWeight;
    return { i: l, j: coords.length, invTotalWeight: totalWeight, invTotalWeightFFFF: totalWeight / 0xffff };
  });
  return { sources, coords, weights };
}

/** scale.go `ftou`: [0, 1] → [0, 0xffff], int32 truncation of x + 0.5. */
function ftou(f: number): number {
  const i = Math.trunc(0xffff * f + 0.5);
  if (i > 0xffff) return 0xffff;
  return i > 0 ? i : 0;
}

/** CatmullRom.Scale of a Gray source to dw x dh, read back as grey (r = g = b). */
function scaleGray(src: GrayImage, dw: number, dh: number): Uint8Array {
  const sw = src.width;
  const sh = src.height;
  const h = newDistrib(dw, sw);
  const v = newDistrib(dh, sh);
  // scaleX_Gray: columns distributed into a dw x sh buffer (alpha is 1).
  const tmp = new Float64Array(dw * sh);
  let t = 0;
  for (let y = 0; y < sh; y += 1) {
    for (const s of h.sources) {
      let pr = 0;
      for (let k = s.i; k < s.j; k += 1) {
        pr += src.pix[y * sw + h.coords[k]] * 0x101 * h.weights[k];
      }
      tmp[t] = pr * s.invTotalWeightFFFF;
      t += 1;
    }
  }
  // scaleY_RGBA_Src: rows distributed into dw x dh.
  const out = new Uint8Array(dw * dh);
  for (let dx = 0; dx < dw; dx += 1) {
    v.sources.forEach((s, dy) => {
      let pr = 0;
      let pa = 0;
      for (let k = s.i; k < s.j; k += 1) {
        pr += tmp[v.coords[k] * dw + dx] * v.weights[k];
        pa += 1 * v.weights[k];
      }
      if (pr > pa) pr = pa;
      out[dy * dw + dx] = ftou(pr * s.invTotalWeight) >> 8;
    });
  }
  return out;
}

/** raster.go `toGrayFitted`: fit, never upscale, centre on a white canvas `maxW` wide. */
export function toGrayFitted(src: GrayImage, maxW: number, maxH: number): GrayImage {
  if (src.width === 0 || src.height === 0) {
    return { width: maxW, height: 1, pix: new Uint8Array(maxW) };
  }
  let scale = 1;
  const fw = maxW / src.width;
  if (fw < scale) scale = fw;
  const fh = maxH / src.height;
  if (fh < scale) scale = fh;
  const w = Math.max(Math.trunc(src.width * scale), 1);
  const h = Math.max(Math.trunc(src.height * scale), 1);

  const scaled = scaleGray(src, w, h);
  const canvas = new Uint8Array(maxW * h).fill(255);
  const offset = Math.trunc((maxW - w) / 2);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const cx = offset + x;
      if (cx >= 0 && cx < maxW) canvas[y * maxW + cx] = scaled[y * w + x];
    }
  }
  return { width: maxW, height: h, pix: canvas };
}

/** raster.go `dither`: Floyd–Steinberg in place, every pixel 0 or 255. */
export function dither(img: GrayImage): void {
  const { width: w, height: h, pix } = img;
  if (w === 0 || h === 0) return;
  const err = new Float64Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const old = pix[i] + err[i];
      const next = old < 128 ? 0 : 255;
      pix[i] = next;
      const e = old - next;
      const spread = (dx: number, dy: number, factor: number) => {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || nx >= w || ny >= h) return;
        err[ny * w + nx] += e * factor;
      };
      spread(1, 0, 7 / 16);
      spread(-1, 1, 3 / 16);
      spread(0, 1, 5 / 16);
      spread(1, 1, 1 / 16);
    }
  }
}

/** A raster as the grid carries it: GS v 0 rows packed MSB-first, as hex. */
export interface LogoRaster {
  readonly width: number;
  readonly height: number;
  readonly bits: string;
}

/** raster.go `escposRaster`, the bits only (the band headers carry nothing the paper shows). */
function packRaster(img: GrayImage, dots: number): LogoRaster {
  const bytesPerRow = Math.trunc((dots + 7) / 8);
  let hex = '';
  for (let y = 0; y < img.height; y += 1) {
    for (let xb = 0; xb < bytesPerRow; xb += 1) {
      let packed = 0;
      for (let bit = 0; bit < 8; bit += 1) {
        const x = xb * 8 + bit;
        if (x >= dots || x >= img.width) continue;
        if (img.pix[y * img.width + x] < 128) packed |= 1 << (7 - bit);
      }
      hex += packed.toString(16).padStart(2, '0');
    }
  }
  return { width: bytesPerRow * 8, height: img.height, bits: hex };
}

/**
 * raster.go `renderLogo`: the dots a logo prints at this paper width, or null
 * when the printer has no raster path (76 mm) — the text alternative prints.
 */
export function renderLogoRaster(src: GrayImage, paperWidthMm: number): LogoRaster | null {
  const dots = rasterDotsForPaperWidth(paperWidthMm);
  if (dots === null) return null;
  const fitted = toGrayFitted(src, dots, LOGO_MAX_HEIGHT_DOTS);
  dither(fitted);
  return packRaster(fitted, dots);
}

// ─── Greyscale PNG decode ───────────────────────────────────────────────────

/** The parts of a PNG this decoder needs; `idat` is the still-compressed image stream. */
export interface PngParts {
  readonly width: number;
  readonly height: number;
  readonly bitDepth: number;
  readonly colorType: number;
  readonly interlace: number;
  readonly idat: Uint8Array;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** Split a PNG into its header and IDAT stream. Null for anything that is not a PNG. */
export function parsePng(bytes: Uint8Array): PngParts | null {
  if (bytes.length < 8 || PNG_SIGNATURE.some((b, i) => bytes[i] !== b)) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 8;
  let header: Omit<PngParts, 'idat'> | null = null;
  const parts: Uint8Array[] = [];
  while (pos + 8 <= bytes.length) {
    const len = view.getUint32(pos);
    const type = String.fromCharCode(bytes[pos + 4], bytes[pos + 5], bytes[pos + 6], bytes[pos + 7]);
    const data = bytes.subarray(pos + 8, pos + 8 + len);
    if (type === 'IHDR') {
      const d = new DataView(data.buffer, data.byteOffset, data.byteLength);
      header = {
        width: d.getUint32(0),
        height: d.getUint32(4),
        bitDepth: data[8],
        colorType: data[9],
        interlace: data[12],
      };
    } else if (type === 'IDAT') {
      parts.push(data);
    } else if (type === 'IEND') {
      break;
    }
    pos += 12 + len;
  }
  if (!header) return null;
  const idat = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    idat.set(p, o);
    o += p.length;
  }
  return { ...header, idat };
}

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  return pb <= pc ? b : c;
}

/**
 * The grey pixels of an 8-bit greyscale, non-interlaced PNG (colour type 0) —
 * exactly what Go's image/png yields as *image.Gray, which is the shape the
 * server writes. `inflated` is the zlib-decompressed IDAT stream. Null for any
 * other PNG: the gateway takes a different (non-Gray) scaling path for those,
 * and the preview will not pretend to reproduce it.
 */
export function decodeGrayPng(parts: PngParts, inflated: Uint8Array): GrayImage | null {
  if (parts.colorType !== 0 || parts.bitDepth !== 8 || parts.interlace !== 0) return null;
  const { width: w, height: h } = parts;
  if (inflated.length < h * (w + 1)) return null;
  const pix = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const filter = inflated[y * (w + 1)];
    const row = inflated.subarray(y * (w + 1) + 1, (y + 1) * (w + 1));
    for (let x = 0; x < w; x += 1) {
      const a = x > 0 ? pix[y * w + x - 1] : 0;
      const b = y > 0 ? pix[(y - 1) * w + x] : 0;
      const c = x > 0 && y > 0 ? pix[(y - 1) * w + x - 1] : 0;
      let v = row[x];
      switch (filter) {
        case 0:
          break;
        case 1:
          v += a;
          break;
        case 2:
          v += b;
          break;
        case 3:
          v += (a + b) >> 1;
          break;
        case 4:
          v += paeth(a, b, c);
          break;
        default:
          return null;
      }
      pix[y * w + x] = v & 0xff;
    }
  }
  return { width: w, height: h, pix };
}

/** base64 → bytes, in the browser and in Node alike. */
export function base64Bytes(b64: string): Uint8Array | null {
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/**
 * Decode a greyscale PNG in the browser (DecompressionStream inflates the
 * zlib stream). Null when it is not one this preview can reproduce exactly.
 */
export async function decodeGrayPngBase64(b64: string): Promise<GrayImage | null> {
  const bytes = base64Bytes(b64);
  const parts = bytes ? parsePng(bytes) : null;
  if (!parts || typeof DecompressionStream === 'undefined') return null;
  try {
    const idat = parts.idat;
    const stream = new ReadableStream<Uint8Array>({
      start(controller) {
        controller.enqueue(idat);
        controller.close();
      },
    }).pipeThrough(new DecompressionStream('deflate') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
    const inflated = new Uint8Array(await new Response(stream).arrayBuffer());
    return decodeGrayPng(parts, inflated);
  } catch {
    return null;
  }
}
